import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import RetailerLayout from '../components/RetailerLayout';
import { Button } from '../components/ui/button';
import { AlertCircle } from 'lucide-react';
import api from '../lib/api';

/**
 * DynamicPage V4 - 100% Dynamique avec fallback multi-slug
 * 
 * Charge automatiquement la page via son slug
 * Utilise RetailerLayout pour affichage uniforme
 * Fallback: essaie /dashboard/{slug}, puis /{slug}
 * Support des sous-routes (ex: /nouveautes/en-cours)
 */
const DynamicPage = () => {
  const { slug, '*': subPath } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const fullSlug = subPath ? `${slug}/${subPath}` : slug;

  useEffect(() => {
    if (!fullSlug) { setLoading(false); return; }
    
    const loadPage = async () => {
      setLoading(true);
      setError(null);
      
      // Try /dashboard/{slug} first (primary), then /{slug} as fallback
      const slugsToTry = [
        `/dashboard/${fullSlug}`,
        `/${fullSlug}`
      ];
      
      for (const trySlug of slugsToTry) {
        try {
          const response = await api.get(`/pages/slug${trySlug}`);
          const pageData = response.data;
          const activeBlocks = (pageData.blocks || []).filter(b => b.is_active);
          setBlocks(activeBlocks);
          setLoading(false);
          return; // Success — stop trying
        } catch {
          // Continue to next slug variant
        }
      }
      
      // All attempts failed
      setError('Page non trouvée');
      setBlocks([]);
      setLoading(false);
    };
    
    loadPage();
  }, [fullSlug]);

  // Chargement
  if (loading) {
    return (
      <RetailerLayout>
        <div className="flex items-center justify-center py-20 min-h-[60vh]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </RetailerLayout>
    );
  }

  if (error) {
    return (
      <RetailerLayout>
        <div className="flex flex-col items-center justify-center py-20 px-4 min-h-[60vh]">
          <div className="bg-red-50 border border-red-200 rounded-lg p-8 max-w-md text-center">
            <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">
              Erreur de chargement
            </h2>
            <p className="text-muted-foreground mb-6">
              Impossible de charger la page &laquo;{fullSlug}&raquo;.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Retour au tableau de bord
            </Button>
          </div>
        </div>
      </RetailerLayout>
    );
  }

  // Cas 2: Page non trouvée ou sans blocs
  if (!blocks || blocks.length === 0) {
    return (
      <RetailerLayout>
        <div className="flex flex-col items-center justify-center py-20 px-4 min-h-[60vh]">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-8 max-w-md text-center">
            <AlertCircle className="h-12 w-12 text-yellow-600 mx-auto mb-4" />
            <h2 className="text-2xl font-heading font-bold text-gray-900 mb-2">
              Page non disponible
            </h2>
            <p className="text-muted-foreground mb-6">
              La page &laquo;{fullSlug}&raquo; n&apos;existe pas ou n&apos;a pas encore de contenu.
            </p>
            <Button onClick={() => navigate('/dashboard')} className="w-full">
              Retour au tableau de bord
            </Button>
          </div>
        </div>
      </RetailerLayout>
    );
  }

  return <RetailerLayout />;
};

export default DynamicPage;